/**
 * ExternalLoginPanel — branded login panel for external portal views.
 *
 * Rendered by ExternalViewPage when an unauthenticated user visits /ext/:token.
 * Theme CSS variables are already injected by ExternalViewPage before this
 * component mounts, so all var(--primary) etc. render in tenant brand colors.
 *
 * planTEK Wave 1 — Phase 10d.4: Branded portal entry experience.
 */
import { useState, useCallback } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase.ts';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';
import type { TenantTheme } from '@/lib/theme';

interface ExternalLoginPanelProps {
  /** View name shown in the login heading */
  viewName: string;
  /** Tenant branding from meta.theme.branding */
  branding?: TenantTheme['branding'];
}

type PanelMode = 'login' | 'signup' | 'forgot';

function getFirebaseErrorMessage(message: string): string {
  if (message.includes('user-not-found') || message.includes('wrong-password') || message.includes('invalid-credential')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (message.includes('email-already-in-use')) {
    return 'An account with this email already exists. Sign in instead.';
  }
  if (message.includes('weak-password')) {
    return 'Password must be at least 6 characters.';
  }
  if (message.includes('invalid-email')) {
    return 'Please enter a valid email address.';
  }
  if (message.includes('too-many-requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (message.includes('user-disabled')) {
    return 'This account has been disabled. Please contact support.';
  }
  return message;
}

export function ExternalLoginPanel({ viewName, branding }: ExternalLoginPanelProps) {
  const { signIn, signInWithEmail, createUserWithEmail, isLoading, clearError } = useAuth();

  const [mode, setMode] = useState<PanelMode>('login');
  const [authTab, setAuthTab] = useState<'email' | 'google'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const clearErrors = useCallback(() => {
    setError(null);
    clearError();
  }, [clearError]);

  const handleGoogleSignIn = useCallback(async () => {
    clearErrors();
    try {
      await signIn();
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [signIn, clearErrors]);

  const handleEmailSignIn = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    try {
      await signInWithEmail(email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [email, password, signInWithEmail, clearErrors]);

  const handleSignUp = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      await createUserWithEmail(email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [email, password, confirmPassword, createUserWithEmail, clearErrors]);

  const handleForgotPassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    } finally {
      setResetLoading(false);
    }
  }, [email, clearErrors]);

  const switchMode = useCallback((next: PanelMode) => {
    setMode(next);
    setError(null);
    setResetSent(false);
  }, []);

  const logoUrl = branding?.logo_url;
  const companyName = branding?.company_name;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: 'var(--background)' }}
      data-testid="external-login-panel"
    >
      <div className="w-full max-w-sm">
        {/* Branding header */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName || 'Company logo'}
              className="h-12 max-w-[200px] mx-auto mb-4 object-contain"
              data-testid="tenant-logo"
            />
          ) : (
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 text-2xl font-bold select-none"
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground, #fff)',
              }}
              data-testid="tenant-monogram"
            >
              {companyName ? companyName.charAt(0).toUpperCase() : 'P'}
            </div>
          )}

          {companyName && (
            <p
              className="text-sm font-semibold mb-1 truncate"
              style={{ color: 'var(--foreground)' }}
              data-testid="tenant-company-name"
            >
              {companyName}
            </p>
          )}

          <h1
            className="text-xl font-bold"
            style={{ color: 'var(--foreground)' }}
          >
            {mode === 'forgot'
              ? 'Reset your password'
              : mode === 'signup'
              ? 'Set up your account'
              : 'Sign in to view'}
          </h1>

          {mode === 'login' && (
            <p
              className="text-sm mt-1 truncate max-w-[280px] mx-auto"
              style={{ color: 'var(--muted-foreground, #6b7280)' }}
              data-testid="view-name"
            >
              {viewName}
            </p>
          )}
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border shadow-sm p-6"
          style={{
            backgroundColor: 'var(--card, var(--background))',
            borderColor: 'var(--border, #e5e7eb)',
          }}
        >
          {mode === 'forgot' ? (
            <ForgotPasswordForm
              email={email}
              setEmail={setEmail}
              resetSent={resetSent}
              isLoading={resetLoading}
              error={error}
              onSubmit={handleForgotPassword}
              onBack={() => switchMode('login')}
            />
          ) : (
            <>
              {/* Auth tab switcher */}
              <div
                className="flex rounded-lg p-1 mb-5"
                style={{ backgroundColor: 'var(--muted, #f3f4f6)' }}
              >
                {(['email', 'google'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setAuthTab(tab)}
                    className="flex-1 py-2.5 text-sm font-medium rounded-md transition-colors capitalize"
                    style={{
                      backgroundColor: authTab === tab ? 'var(--background)' : 'transparent',
                      color: authTab === tab ? 'var(--foreground)' : 'var(--muted-foreground, #6b7280)',
                      boxShadow: authTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                    data-testid={`tab-${tab}`}
                  >
                    {tab === 'google' ? 'Google' : 'Email'}
                  </button>
                ))}
              </div>

              {/* Error */}
              {error && (
                <div
                  className="mb-4 p-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 10%, transparent)',
                    color: 'var(--destructive, #b91c1c)',
                    border: '1px solid color-mix(in srgb, var(--destructive, #ef4444) 30%, transparent)',
                  }}
                  data-testid="login-error"
                  role="alert"
                >
                  {error}
                </div>
              )}

              {authTab === 'google' ? (
                <GoogleSignInButton isLoading={isLoading} onClick={handleGoogleSignIn} />
              ) : mode === 'signup' ? (
                <SignUpForm
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  isLoading={isLoading}
                  onSubmit={handleSignUp}
                />
              ) : (
                <SignInForm
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  isLoading={isLoading}
                  onSubmit={handleEmailSignIn}
                  onForgotPassword={() => switchMode('forgot')}
                />
              )}

              {/* Mode switcher link */}
              <div
                className="mt-4 text-center text-sm"
                style={{ color: 'var(--muted-foreground, #6b7280)' }}
              >
                {mode === 'login' ? (
                  <>
                    First time?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('signup')}
                      className="font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                      style={{ color: 'var(--primary)' }}
                      data-testid="setup-account-link"
                    >
                      Set up your account
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                      style={{ color: 'var(--primary)' }}
                      data-testid="sign-in-link"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <PoweredByFooter />
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface SignInFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
}

function SignInForm({ email, setEmail, password, setPassword, isLoading, onSubmit, onForgotPassword }: SignInFormProps) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <FormField
        id="ext-login-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testId="email-input"
        required
      />
      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            htmlFor="ext-login-password"
            className="block text-sm font-medium"
            style={{ color: 'var(--foreground)' }}
          >
            Password
          </label>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-xs font-medium hover:underline focus:outline-none focus-visible:ring-2"
            style={{ color: 'var(--primary)' }}
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </div>
        <input
          id="ext-login-password"
          data-testid="password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          className="mt-0 block w-full rounded-lg border px-3 py-2 text-base sm:text-sm focus:outline-none focus-visible:ring-2"
          style={{
            backgroundColor: 'var(--background)',
            borderColor: 'var(--border, #d1d5db)',
            color: 'var(--foreground)',
          }}
        />
      </div>
      <PrimaryButton type="submit" disabled={!email || !password || isLoading} isLoading={isLoading} testId="sign-in-button">
        Sign in
      </PrimaryButton>
    </form>
  );
}

interface SignUpFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

function SignUpForm({ email, setEmail, password, setPassword, confirmPassword, setConfirmPassword, isLoading, onSubmit }: SignUpFormProps) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <FormField
        id="ext-signup-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testId="signup-email-input"
        required
      />
      <FormField
        id="ext-signup-password"
        label="Password"
        type="password"
        value={password}
        onChange={setPassword}
        placeholder="Min. 8 characters"
        testId="signup-password-input"
        required
      />
      <FormField
        id="ext-signup-confirm"
        label="Confirm password"
        type="password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        placeholder="Re-enter password"
        testId="signup-confirm-input"
        required
      />
      <PrimaryButton type="submit" disabled={!email || !password || !confirmPassword || isLoading} isLoading={isLoading} testId="create-account-button">
        Create account
      </PrimaryButton>
    </form>
  );
}

interface ForgotPasswordFormProps {
  email: string;
  setEmail: (v: string) => void;
  resetSent: boolean;
  isLoading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}

function ForgotPasswordForm({ email, setEmail, resetSent, isLoading, error, onSubmit, onBack }: ForgotPasswordFormProps) {
  if (resetSent) {
    return (
      <div className="text-center space-y-4" data-testid="reset-sent-confirmation">
        <div
          className="inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto"
          style={{ backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)' }}
        >
          <svg className="w-6 h-6" style={{ color: 'var(--primary)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
          Check your email
        </p>
        <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
          We sent a password reset link to <strong>{email}</strong>. Check your inbox and follow the instructions.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium hover:underline focus:outline-none"
          style={{ color: 'var(--primary)' }}
          data-testid="back-to-login-link"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
        Enter your email and we'll send you a link to reset your password.
      </p>

      {error && (
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 10%, transparent)',
            color: 'var(--destructive, #b91c1c)',
            border: '1px solid color-mix(in srgb, var(--destructive, #ef4444) 30%, transparent)',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      <FormField
        id="ext-forgot-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testId="forgot-email-input"
        required
      />

      <PrimaryButton type="submit" disabled={!email || isLoading} isLoading={isLoading} testId="send-reset-button">
        Send reset link
      </PrimaryButton>

      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium hover:underline focus:outline-none"
          style={{ color: 'var(--muted-foreground, #6b7280)' }}
          data-testid="back-to-login-link"
        >
          Back to sign in
        </button>
      </div>
    </form>
  );
}

// ── Shared primitives ────────────────────────────────────────────────────────

interface FormFieldProps {
  id: string;
  label: string;
  type: 'email' | 'password' | 'text';
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
  required?: boolean;
}

function FormField({ id, label, type, value, onChange, placeholder, testId, required }: FormFieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium mb-1"
        style={{ color: 'var(--foreground)' }}
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="block w-full rounded-lg border px-3 py-2 text-base sm:text-sm focus:outline-none focus-visible:ring-2"
        style={{
          backgroundColor: 'var(--background)',
          borderColor: 'var(--border, #d1d5db)',
          color: 'var(--foreground)',
        }}
      />
    </div>
  );
}

interface PrimaryButtonProps {
  type?: 'submit' | 'button';
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  testId?: string;
}

function PrimaryButton({ type = 'button', disabled, isLoading, onClick, children, testId }: PrimaryButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-center rounded-lg px-4 py-3 min-h-[44px] text-sm font-semibold transition-opacity focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor: 'var(--primary)',
        color: 'var(--primary-foreground, #fff)',
      }}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
          Please wait...
        </span>
      ) : children}
    </button>
  );
}

interface GoogleSignInButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

function GoogleSignInButton({ isLoading, onClick }: GoogleSignInButtonProps) {
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClick}
        disabled={isLoading}
        data-testid="google-sign-in-button"
        className="w-full flex items-center justify-center gap-3 rounded-lg border px-4 py-3 min-h-[44px] text-sm font-medium transition-colors hover:bg-opacity-50 focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: 'var(--background)',
          borderColor: 'var(--border, #d1d5db)',
          color: 'var(--foreground)',
        }}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
            Signing in...
          </span>
        ) : (
          <>
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </>
        )}
      </button>
      <p className="text-center text-xs" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
        Sign in with your Google account
      </p>
    </div>
  );
}
